import React, { useEffect, useState, useContext, useRef } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';

import moment from 'moment';

import styles from '../assets/styles/DrawDown.module.scss';
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb';
import Layout from '../components/common/Layout';
import { UserContext } from '../context/UserContext';
import { TRANSFER_OUT_FEE } from '../helpers/constants';
import { formatUSDCurrency } from '../helpers/format';
import { getLoanActivities, getLoans } from '../service/loan';

const ActivityDetails = () => {
  const [loans, setLoans] = useState([]);
  const [activities, setActivities] = useState([]);
  const { loading, setLoading } = useContext(UserContext);
  const params = useParams();
  const loanId = params.id;
  const activityId = params.activityId;
  const navigate = useNavigate();
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    const loadActivities = async (id) => {
      try {
        const res = await getLoanActivities(id);
        const activities = res.data.reverse();
        setActivities(activities);
      } catch (e) {
        console.log('error', e);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadActivities(loanId);
  }, [loans]);

  useEffect(() => {
    const loadLoans = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getLoans();

        setLoans(data);
      } catch (e) {
        console.log(e);
        navigate('/dashboard');
      }
    };
    loadLoans();
  }, []);

  const renderTypeDetails = (activity) => {
    switch (activity.type) {
      case 'Draw Down':
        return (
          <>
            <p className={styles.detailsHelper}>
              Status: <strong>{activity.drawDown?.status}</strong>
            </p>
          </>
        );
      case 'Payment':
        return (
          <>
            <p className={styles.detailsHelper}>
              Status: <strong>{activity.status}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Payment Number: <strong>{activity.payment?.paymentNumber}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Payment Schedule Percentage:{' '}
              <strong>{activity.payment?.paymentSchedulePercentage}</strong>
            </p>
          </>
        );
      case 'Transfer Out':
        return (
          <>
            <p className={styles.detailsHelper}>
              Status: <strong>{activity.status}</strong>
            </p>
          </>
        );
      default:
        break;
    }
  };

  const renderAmountDetails = (activity) => {
    switch (activity.type) {
      case 'Draw Down':
        return (
          <>
            <p className={styles.detailsHelper}>
              Drawdown fee:{' '}
              <strong>{formatUSDCurrency(activity.drawDown?.drawFees, 2, true)}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Reserve Amount:{' '}
              <strong>{formatUSDCurrency(activity.drawDown?.reserveAmount, 2, true)}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Disbursed Amount:{' '}
              <strong>{formatUSDCurrency(activity.drawDown?.disbursedAmount, 2, true)}</strong>
            </p>
          </>
        );
      case 'Payment':
        return (
          <>
            <p className={styles.detailsHelper}>
              Net Payment to Administrator:{' '}
              <strong>
                {formatUSDCurrency(activity.payment?.netPaymentToAdministrator, 2, true)}
              </strong>
            </p>
            <p className={styles.detailsHelper}>
              Net Payment to Contractor:{' '}
              <strong>
                {formatUSDCurrency(activity.payment?.netPaymentToContractor, 2, true)}
              </strong>
            </p>
            <p className={styles.detailsHelper}>
              Payment From Reserve Account:{' '}
              <strong>
                {formatUSDCurrency(activity.payment?.paymentFromReserveAccount, 2, true)}
              </strong>
            </p>
            <p className={styles.detailsHelper}>
              Payment to Contractor:{' '}
              <strong>{formatUSDCurrency(activity.payment?.paymentToContractor, 2, true)}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Principal Payment to Administrator:{' '}
              <strong>
                {formatUSDCurrency(activity.payment?.principalPaymentToAdministrator, 2, true)}
              </strong>
            </p>
            <p className={styles.detailsHelper}>
              Starting Reserve:{' '}
              <strong>{formatUSDCurrency(activity.payment?.startingReserve, 2, true)}</strong>
            </p>
            <p className={styles.detailsHelper}>
              Total Amount Due To Administrator:{' '}
              <strong>
                {formatUSDCurrency(activity.payment?.totalAmountDueToAdministrator, 2, true)}
              </strong>
            </p>
          </>
        );

      default:
        break;
    }
  };

  const renderTransactionId = (activity) => {
    switch (activity.type) {
      case 'Draw Down':
        return <p className={styles.detailsHelper}>{activity.drawDown?.disbursementId}</p>;
      case 'Payment':
        return <p className={styles.detailsHelper}>{activity.payment?.disbursementId}</p>;
      default:
        break;
    }
  };

  const Details = React.forwardRef((props, ref) => {
    return (
      <div className={styles.activityDetailsWrapper__topSeciton} ref={ref}>
        <div className={styles.activityDetailsWrapper__topSeciton_drdwn}>
          <div>
            <p className={styles.detailsHeader}>{activities[activityId]?.type}</p>
            {renderTypeDetails(activities[activityId])}
            <p className={styles.dateDetailsHelper}>
              {moment(activities[activityId]?.date).format('DD MMMM, YYYY   hh:mm A')}
            </p>
          </div>
          <div>
            <p className={styles.detailsHeader}>
              Amount{' '}
              <span className={styles.detailsAmount}>
                {formatUSDCurrency(activities[activityId]?.amount, 2, true)}
              </span>
            </p>
            {renderAmountDetails(activities[activityId])}
          </div>
        </div>
        <div>
          {activities[activityId]?.type !== 'Transfer Out' && (
            <>
              <p className={styles.detailsHeader}>Transaction ID</p>
              {renderTransactionId(activities[activityId])}
            </>
          )}
        </div>
      </div>
    );
  });

  const renderContent = () => {
    return (
      !loading &&
      activities[activityId] && (
        <div className={`${styles.DrawDown} ${styles.activitiesPage}`}>
          <Breadcrumb className={styles.DrawDown__breadcrumb}>
            <BreadcrumbItem>
              <Link to={`/dashboard`}>Line of Credit</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <span
                style={{ cursor: 'pointer', color: '#462d7a' }}
                onClick={() => {
                  navigate(`/account/${loanId}/recent-activities`);
                }}
              >
                View Activities
              </span>
            </BreadcrumbItem>
            <BreadcrumbItem>View Details</BreadcrumbItem>
          </Breadcrumb>
          <div
            className={styles.arrowLeftWrapper}
            onClick={() => navigate(`/account/${loanId}/recent-activities`)}
          >
            <ArrowLeftOutlined
              style={{
                strokeWidth: '60',
                stroke: '#462d7a',
                fontSize: '16px',
              }}
            />
            <h3 className={styles.tableTitle}>Details</h3>
          </div>
          <div className={styles.activitiesTableWrapper}>
            <div className={styles.activityDetailsWrapper}>
              <Details documentTitle={'actionDetails'} ref={componentRef} />
              <div className={styles.printDetails} onClick={handlePrint}>
                <PrinterOutlined
                  style={{
                    strokeWidth: '20',
                    stroke: '#000',
                    fontSize: '20px',
                  }}
                />
                <p>Print Details</p>
              </div>
            </div>
          </div>
        </div>
      )
    );
  };

  return <Layout>{renderContent()}</Layout>;
};

export default ActivityDetails;
